import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError, API, mrApi, mrSession } from "@/lib/apiClient";
import { displayDoctorName } from "@/lib/utils";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { SPECIALTIES, INDIAN_STATES, STANDARD_PLEDGES } from "@/lib/constants";
import { Textarea } from "@/components/ui/textarea";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Download, CheckCircle2, Loader2, ArrowRight, ArrowLeft, Camera, X, LogOut, KeyRound, UserCheck, ClipboardList } from "lucide-react";
import { fileToResizedDataUrl } from "@/lib/photo";
import PromoPledgePage from "@/pages/PromoPledgePage";
import { usePresence } from "@/lib/usePresence";

const EMPTY = { name: "", reg_no: "", specialty: "", city: "", state: "", email: "", mobile: "", photo: null };

// selfService: unlisted direct-doctor mode (/pledge/<linkName>) — no MR
// login; submissions are attributed to the named link on the backend.
export default function PledgePage({ selfService = false }) {
  const navigate = useNavigate();
  const { linkName } = useParams();
  const [linkValid, setLinkValid] = useState(!selfService);
  const [linkInfo, setLinkInfo] = useState(null);
  const [mr, setMr] = useState(selfService ? null : mrSession.info());
  const [pwdOpen, setPwdOpen] = useState(false);
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [pwdSaving, setPwdSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [pledgeChoice, setPledgeChoice] = useState(STANDARD_PLEDGES[0].id); // id | "custom"
  const [customPledge, setCustomPledge] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});

  // Self-service mode: the named link must be registered (and active).
  useEffect(() => {
    if (!selfService) return;
    api.get(`/pledges/link-info/${linkName}`)
      .then((r) => { setLinkInfo(r.data); setLinkValid(true); })
      .catch(() => setLinkValid(false));
  }, [selfService, linkName]);

  // Pledge page is only for logged-in MRs — no direct access (except the
  // unlisted self-service route).
  useEffect(() => {
    if (selfService) return;
    if (!mrSession.token()) { navigate("/mr/login"); return; }
    mrApi.get("/mr/me")
      .then((r) => { setMr(r.data); mrSession.update(r.data); })
      .catch(() => { mrSession.clear(); navigate("/mr/login"); });
  }, [navigate, selfService]);

  const logoutMr = () => {
    mrSession.clear();
    navigate("/mr/login");
  };

  const changePassword = async () => {
    if (newPwd.length < 6) { toast.error("New password must be at least 6 characters."); return; }
    setPwdSaving(true);
    try {
      await mrApi.post("/mr/change-password", { old_password: oldPwd, new_password: newPwd });
      toast.success("Password changed");
      setPwdOpen(false); setOldPwd(""); setNewPwd("");
      setMr((m) => ({ ...m, must_change_password: false }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not change password");
    } finally {
      setPwdSaving(false);
    }
  };

  usePresence(selfService ? `link:${linkName}` : "mr-form");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const dataUrl = await fileToResizedDataUrl(file);
      set("photo", dataUrl);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Enter your full name";
    if (form.reg_no.trim().length < 2) e.reg_no = "Enter your registration number";
    if (!form.specialty) e.specialty = "Select your specialty";
    if (form.city.trim().length < 2) e.city = "Enter your city";
    if (!form.state) e.state = "Select your state";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!/^\d{6,15}$/.test(form.mobile.replace(/\s+/g, ""))) e.mobile = "Enter a valid mobile number";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const chosenPledgeText =
    pledgeChoice === "custom"
      ? customPledge.trim()
      : STANDARD_PLEDGES.find((p) => p.id === pledgeChoice)?.text || "";

  const submit = async () => {
    if (!validate()) { toast.error("Please fix the highlighted fields."); return; }
    if (pledgeChoice === "custom" && customPledge.trim().length < 20) {
      toast.error("Please write your pledge (at least 20 characters).");
      return;
    }
    if (!agreed) { toast.error("Please confirm the pledge to continue."); return; }
    setSubmitting(true);
    try {
      const res = selfService
        ? await api.post(`/pledges/via/${linkName}`, { ...form, pledge_text: chosenPledgeText })
        : await mrApi.post("/pledges", { ...form, pledge_text: chosenPledgeText });
      setResult(res.data);
      toast.success("Pledge submitted successfully!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      if (!selfService && err.response?.status === 401) {
        mrSession.clear();
        toast.error("Session expired — please log in again.");
        navigate("/mr/login");
        return;
      }
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="App bg-slate-50 min-h-screen">
        <Navbar />
        <div className="max-w-2xl mx-auto px-5 py-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            data-testid="pledge-success"
            className="rounded-3xl bg-white border border-slate-200 p-8 sm:p-12 text-center shadow-sm"
          >
            <div className="tricolor-bar h-1.5 w-24 mx-auto rounded-full mb-8" />
            <div className="grid place-items-center w-20 h-20 rounded-full bg-green-50 mx-auto mb-6">
              <CheckCircle2 className="w-11 h-11 text-[#138808]" />
            </div>
            <h1 className="font-display font-extrabold text-3xl text-slate-900">Thank you, {displayDoctorName(result.name)}!</h1>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Your pledge has been recorded on the Healthy India Pledge Wall.
            </p>
            <div className="mt-6 rounded-xl bg-slate-50 border border-slate-200 p-4">
              <p className="text-xs uppercase tracking-wider text-slate-500">Certificate ID</p>
              <p data-testid="certificate-id" className="font-display font-bold text-xl text-[#0056D2]">{result.certificate_id}</p>
            </div>
            <a
              href={`${API}/certificate/${result.certificate_id}`}
              data-testid="download-certificate-btn"
              className="mt-8 inline-flex"
            >
              <Button size="lg" className="rounded-full bg-[#0056D2] hover:bg-blue-700 text-white font-semibold px-7 h-12">
                <Download className="w-4 h-4 mr-2" /> Download Certificate
              </Button>
            </a>
            <div className="mt-6 flex items-center justify-center gap-4 text-sm">
              <Link to="/wall" className="text-[#0056D2] font-medium hover:underline" data-testid="view-wall-link">
                See it on the live wall <ArrowRight className="w-3.5 h-3.5 inline" />
              </Link>
            </div>
          </motion.div>
        </div>
        <Footer />
      </div>
    );
  }

  // Promotional links get the plain branded page (no navbar/footer).
  if (selfService && linkInfo?.promo) {
    return <PromoPledgePage linkName={linkInfo.name} brandLogo={linkInfo.brand_logo} />;
  }

  if (selfService && !linkValid) {
    return (
      <div className="App bg-slate-50 min-h-screen">
        <Navbar />
        <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="invalid-link">
          <h1 className="font-display font-extrabold text-2xl text-slate-900">This pledge link is not valid</h1>
          <p className="mt-3 text-slate-500">Please check the link you received, or contact the campaign team.</p>
        </div>
        <Footer />
      </div>
    );
  }

  const field = (id, label, node) => (
    <div>
      <Label htmlFor={id} className="text-sm font-medium text-slate-700">{label}</Label>
      <div className="mt-1.5">{node}</div>
      {errors[id] && <p data-testid={`error-${id}`} className="text-xs text-red-600 mt-1">{errors[id]}</p>}
    </div>
  );

  return (
    <div className="App bg-slate-50 min-h-screen">
      <Navbar />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0056D2]" data-testid="back-home-link">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          {mr && (
            <div data-testid="mr-bar" className="w-full sm:w-auto rounded-2xl sm:rounded-full bg-white border border-slate-200 px-3 sm:pl-4 sm:pr-2 py-2 sm:py-1.5 shadow-sm">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="flex items-center gap-1.5 min-w-0">
                  <UserCheck className="w-4 h-4 text-[#138808] shrink-0" />
                  <span className="text-sm font-semibold text-slate-800 truncate">{mr.name}</span>
                  <span className="text-xs text-slate-400 whitespace-nowrap">MR · {mr.emp_code}</span>
                </span>
                <span className="flex items-center gap-1 ml-auto">
                  <Link to="/mr/pledges" data-testid="my-pledges-link">
                    <Button size="sm" variant="ghost" className="rounded-full h-8 text-[#0056D2] px-2.5">
                      <ClipboardList className="w-3.5 h-3.5 mr-1" /> My Pledges
                    </Button>
                  </Link>
                  <Link to="/mr/self-pledge" data-testid="self-pledge-link">
                    <Button size="sm" variant="ghost" className="rounded-full h-8 text-[#138808] px-2.5">
                      <UserCheck className="w-3.5 h-3.5 mr-1" /> Self Pledge
                    </Button>
                  </Link>
                  <Button data-testid="mr-change-pwd-btn" onClick={() => setPwdOpen(true)} size="sm" variant="ghost" className="rounded-full h-8 text-slate-500 px-2.5">
                    <KeyRound className="w-3.5 h-3.5 mr-1" /> Password
                  </Button>
                  <Button data-testid="mr-logout-btn" onClick={logoutMr} size="sm" variant="ghost" className="rounded-full h-8 text-red-600 px-2.5">
                    <LogOut className="w-3.5 h-3.5 mr-1" /> Logout
                  </Button>
                </span>
              </div>
            </div>
          )}
        </div>
        {mr?.must_change_password && (
          <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            You are using the default password (your Employee ID). Please{" "}
            <button type="button" className="font-bold underline" onClick={() => setPwdOpen(true)}>change your password</button>.
          </div>
        )}
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 rounded-3xl bg-white border border-slate-200 p-6 sm:p-8">
            <h1 className="font-display font-extrabold text-3xl text-slate-900">Take the Pledge</h1>
            <p className="mt-2 text-slate-500">Registered medical practitioners only. All fields required.</p>

            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              {field("name", "Full Name",
                <Input data-testid="input-name" id="name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Dr. Full Name" />)}
              {field("reg_no", "Medical Council Reg. No.",
                <Input data-testid="input-reg_no" id="reg_no" value={form.reg_no} onChange={(e) => set("reg_no", e.target.value)} placeholder="e.g. MH/12345/2015" />)}
              {field("specialty", "Specialty",
                <Select value={form.specialty} onValueChange={(v) => set("specialty", v)}>
                  <SelectTrigger data-testid="input-specialty" id="specialty"><SelectValue placeholder="Select specialty" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {SPECIALTIES.map((s) => <SelectItem key={s} value={s} data-testid={`specialty-${s}`}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>)}
              {field("city", "City",
                <Input data-testid="input-city" id="city" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="e.g. Mumbai" />)}
              {field("state", "State / UT",
                <Select value={form.state} onValueChange={(v) => set("state", v)}>
                  <SelectTrigger data-testid="input-state" id="state"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {INDIAN_STATES.map((s) => <SelectItem key={s} value={s} data-testid={`state-${s}`}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>)}
              {field("email", "Email",
                <Input data-testid="input-email" id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" />)}
              {field("mobile", "Mobile Number",
                <Input data-testid="input-mobile" id="mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="10-digit mobile" />)}
            </div>

            <div className="mt-6">
              <Label className="text-sm font-medium text-slate-700">Your Photo <span className="text-slate-400 font-normal">(optional — shown on the live pledge wall)</span></Label>
              <div className="mt-2 flex items-center gap-4">
                {form.photo ? (
                  <div className="relative">
                    <img src={form.photo} alt="Preview" data-testid="pledge-photo-preview" className="w-20 h-20 rounded-full object-cover ring-2 ring-[#FF9933]" />
                    <button
                      type="button"
                      data-testid="pledge-photo-remove"
                      onClick={() => set("photo", null)}
                      aria-label="Remove photo"
                      className="absolute -top-1 -right-1 grid place-items-center w-6 h-6 rounded-full bg-red-500 text-white shadow"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {/* `capture` forces the camera app to open directly — without
                        it some Android pickers never offer a camera option. */}
                    <label className="inline-flex items-center gap-2 rounded-full bg-[#0056D2] text-white text-sm font-semibold px-5 py-2.5 cursor-pointer hover:bg-blue-700 transition-colors">
                      <Camera className="w-4 h-4" /> Take Photo
                      <input
                        data-testid="pledge-photo-capture"
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="sr-only"
                        onChange={onPhotoChange}
                      />
                    </label>
                    <label className="inline-flex items-center gap-2 rounded-full border border-slate-300 text-slate-600 text-sm font-semibold px-5 py-2.5 cursor-pointer hover:border-[#0056D2] hover:text-[#0056D2] transition-colors">
                      🖼️ Upload from Gallery
                      <input
                        data-testid="pledge-photo-input"
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={onPhotoChange}
                      />
                    </label>
                  </div>
                )}
                <p className="text-xs text-slate-500 max-w-[220px]">
                  A clear front-facing photo works best. It appears on your card in the India map wall.
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-3xl bg-slate-950 text-white p-6 sm:p-8">
              <div className="tricolor-bar h-1 w-16 rounded-full mb-5" />
              <h2 className="font-display font-bold text-xl">Choose Your Pledge</h2>
              <p className="mt-1 text-xs text-slate-400">Pick a ready pledge or write your own.</p>

              <div className="mt-4 space-y-2.5" data-testid="pledge-options">
                {STANDARD_PLEDGES.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`pledge-option-${p.id}`}
                    onClick={() => setPledgeChoice(p.id)}
                    className={`w-full text-left rounded-xl border p-3.5 transition ${
                      pledgeChoice === p.id
                        ? "border-[#FF9933] bg-[#FF9933]/10"
                        : "border-white/15 bg-white/5 hover:border-white/40"
                    }`}
                  >
                    <p className={`text-xs font-bold uppercase tracking-wide ${pledgeChoice === p.id ? "text-[#FF9933]" : "text-slate-400"}`}>
                      {p.title}
                    </p>
                    <p className="mt-1 text-[13px] text-slate-200 leading-snug italic">“{p.text}”</p>
                  </button>
                ))}
                <button
                  type="button"
                  data-testid="pledge-option-custom"
                  onClick={() => setPledgeChoice("custom")}
                  className={`w-full text-left rounded-xl border p-3.5 transition ${
                    pledgeChoice === "custom"
                      ? "border-[#FF9933] bg-[#FF9933]/10"
                      : "border-white/15 bg-white/5 hover:border-white/40"
                  }`}
                >
                  <p className={`text-xs font-bold uppercase tracking-wide ${pledgeChoice === "custom" ? "text-[#FF9933]" : "text-slate-400"}`}>
                    ✍️ Write my own pledge
                  </p>
                  {pledgeChoice === "custom" && (
                    <Textarea
                      data-testid="custom-pledge-input"
                      value={customPledge}
                      onChange={(e) => setCustomPledge(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      maxLength={1000}
                      rows={4}
                      placeholder="Type your pledge in English or your regional language…"
                      className="mt-2 bg-white/10 border-white/20 text-white placeholder:text-slate-500 text-sm"
                    />
                  )}
                </button>
              </div>
              <label className="mt-6 flex items-start gap-3 cursor-pointer">
                <Checkbox data-testid="pledge-agree-checkbox" checked={agreed} onCheckedChange={setAgreed} className="mt-0.5 border-white/40 data-[state=checked]:bg-[#FF9933] data-[state=checked]:border-[#FF9933]" />
                <span className="text-sm text-slate-200">I read and take this pledge as a registered medical practitioner (self-declared).</span>
              </label>
              <Button
                data-testid="submit-pledge-button"
                onClick={submit}
                disabled={submitting}
                size="lg"
                className="mt-6 w-full rounded-full bg-[#FF9933] hover:bg-orange-500 text-slate-900 font-bold h-12"
              >
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : "I Pledge"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* MR change-password dialog */}
      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogTitle className="font-display font-bold text-xl text-slate-900">Change Password</DialogTitle>
          <div className="space-y-4">
            <div>
              <Label htmlFor="old-pwd" className="text-sm font-medium text-slate-700">Current password</Label>
              <Input data-testid="mr-old-pwd" id="old-pwd" type="password" value={oldPwd} onChange={(e) => setOldPwd(e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="new-pwd" className="text-sm font-medium text-slate-700">New password (min 6 characters)</Label>
              <Input data-testid="mr-new-pwd" id="new-pwd" type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="mt-1.5" />
            </div>
            <Button data-testid="mr-save-pwd-btn" onClick={changePassword} disabled={pwdSaving} className="w-full rounded-full bg-[#0056D2] hover:bg-blue-700 text-white font-semibold">
              {pwdSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}
